import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, CheckCircle2, Clock, XCircle, Truck, MapPin, RotateCcw, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fmtDate } from '@/lib/utils';

const STATUS_CONFIG = {
  pending:            { label: 'PENDING',        icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  paid:               { label: 'CONFIRMED',      icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  failed:             { label: 'FAILED',         icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-400/10' },
  in_production:      { label: 'IN PRODUCTION',  icon: Package,      color: 'text-blue-400',   bg: 'bg-blue-400/10' },
  packed:             { label: 'PACKED',         icon: Package,      color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
  dispatched:         { label: 'SHIPPED',        icon: Truck,        color: 'text-primary',    bg: 'bg-primary/10' },
  delivered:          { label: 'DELIVERED',      icon: MapPin,       color: 'text-green-400',  bg: 'bg-green-400/10' },
  completed:          { label: 'COMPLETED',      icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  refunded:           { label: 'REFUNDED',       icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
  partially_refunded: { label: 'PART. REFUNDED', icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
};

export default function Orders() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    const results = await base44.entities.Order.filter({ customer_email: email });
    setOrders(results.sort((a, b) => new Date(b.created_date) - new Date(a.created_date)));
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-20 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">TRACK YOUR PURCHASE</p>
          <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
            MY ORDERS
          </h1>
          <p className="text-white/40 text-sm max-w-lg">
            Enter the email address you used at checkout to view your order history.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-14 max-w-3xl mx-auto">
        {/* Email lookup form */}
        <form onSubmit={handleLookup} className="flex gap-3 mb-10">
          <div className="flex-1">
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">EMAIL ADDRESS</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setSubmitted(false); }}
              placeholder="your@email.com"
              className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20"
              required
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 h-11 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              LOOK UP
            </button>
          </div>
        </form>

        {/* Results */}
        <AnimatePresence mode="wait">
          {submitted && (
            <motion.div key="results" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {orders.length === 0 ? (
                <div className="text-center py-16 border border-white/5 bg-[#111115]">
                  <Package className="w-10 h-10 text-white/10 mx-auto mb-4" />
                  <p className="text-white/30 text-sm">No orders found for <span className="text-white/50">{email}</span></p>
                  <p className="text-white/20 text-xs mt-2">Check the email address and try again.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-white/30 text-xs font-bold tracking-widest">{orders.length} ORDER{orders.length !== 1 ? 'S' : ''} FOUND</p>
                  {orders.map((order) => {
                    const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                    const Icon = status.icon;
                    return (
                      <div key={order.id} className="bg-[#111115] border border-white/5 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="text-white font-bold text-sm">{order.product_name}</p>
                            <p className="text-white/30 text-xs mt-1">
                              Order #{order.id?.slice(-8).toUpperCase()} · {fmtDate(order.created_date)}
                            </p>
                          </div>
                          <span className={`flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-1.5 ${status.bg} ${status.color}`}>
                            <Icon className="w-3.5 h-3.5" /> {status.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-white/5">
                          <span className="text-white/40 text-sm">Total</span>
                          <span className="text-primary font-black text-xl">£{order.amount?.toFixed(2)}</span>
                        </div>
                        {order.notes && (
                          <p className="text-white/30 text-xs mt-3 pt-3 border-t border-white/5">{order.notes}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}